import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormContext } from '../../FormContext';
import FieldDescriptor, { ConstantWrapper } from './FieldDescriptor';

const operations = {
  add: <span className="operator">&#x2b;</span>,
  divide: <span className="operator">&#xf7;</span>,
  multiply: <span className="operator">&#xd7;</span>,
  negative: true,
  subtract: <span className="operator">&#x2212;</span>,
  subtractToZero: <span className="operator">&#x2212;</span>,
  sumByCondition: true,
  addsub: true,
  subadd: true,
};

const CalcExplain = ({ fieldName = '', formManager }) => {
  const manager = formManager || useContext(FormContext).manager;

  if (!manager || !manager.layout) {
    return null;
  }

  const formLookup = manager.layout.lookup;
  const field = formLookup[fieldName];

  if (!field || !field.calculate) {
    return null;
  }

  if (!field.calculate.method || !operations[field.calculate.method]) {
    const warning = `Method ${field.calculate.method} not supported`;
    // console.warn(warning);
    return <div>{warning}</div>;
  }

  if (!field.calculate.params || !field.calculate.params.length) {
    return <div>Invalid Calculation</div>;
  }

  if (field.calculate.method === 'negative') {
    return (
      <div className="calc-explain">
        <FieldDescriptor fieldName={field.calculate.params[0]} lookup={formLookup} />
        <span className="operator">&#xd7;</span>
        <ConstantWrapper>-1</ConstantWrapper>
      </div>
    );
  }

  if (field.calculate.method === 'sumByCondition') {
    return (
      <div className="calc-explain">
        <span className="explain mr-2">Sum of</span>
        <FieldDescriptor fieldName={field.calculate.params[0]} lookup={formLookup} />
        <span className="explain mr-2 ml-2">when</span>
        <FieldDescriptor fieldName={field.calculate.params[1]} lookup={formLookup} />
        <span className="explain mr-2 ml-2">is equal to</span>
        <FieldDescriptor fieldName={field.calculate.params[2]} lookup={formLookup} />
      </div>
    );
  }

  if (field.calculate.method === 'addsub') {
    return (
      <div className="calc-explain">
        {field.calculate.params.map((value, index) => {
          const paramValue = String(value);

          // Treat the last in the series differently (ie no operation comes after it)
          if (index === field.calculate.params.length - 1) {
            return <FieldDescriptor key={`${paramValue}${index}`} fieldName={paramValue} lookup={formLookup} />;
          }

          const isNextLast = index + 1 === field.calculate.params.length - 1;

          return (
            <React.Fragment key={`${paramValue}${index}`}>
              <FieldDescriptor fieldName={paramValue} lookup={formLookup} />
              {isNextLast ? operations.subtract : operations.add}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  if (field.calculate.method === 'addsub') {
    return (
      <div className="calc-explain">
        {field.calculate.params.map((value, index) => {
          const paramValue = String(value);

          // Treat the last in the series differently (ie no operation comes after it)
          if (index === field.calculate.params.length - 1) {
            return <FieldDescriptor key={`${paramValue}${index}`} fieldName={paramValue} lookup={formLookup} />;
          }

          const isNextLast = index + 1 === field.calculate.params.length - 1;

          return (
            <React.Fragment key={`${paramValue}${index}`}>
              <FieldDescriptor fieldName={paramValue} lookup={formLookup} />
              {isNextLast ? operations.multiply : operations.add}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  if (field.calculate.method === 'subadd') {
    return (
      <div className="calc-explain">
        {field.calculate.params.map((value, index) => {
          const paramValue = String(value);

          // Treat the last in the series differently (ie no operation comes after it)
          if (index === field.calculate.params.length - 1) {
            return <FieldDescriptor key={`${paramValue}${index}`} fieldName={paramValue} lookup={formLookup} />;
          }

          const isNextLast = index + 1 === field.calculate.params.length - 1;

          return (
            <React.Fragment key={`${paramValue}${index}`}>
              <FieldDescriptor fieldName={paramValue} lookup={formLookup} />
              {isNextLast ? operations.add : operations.subtract}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Mirroring another field
  if (field.calculate.params.length === 1) {
    return (
      <div className="calc-explain">
        <span className="explain mr-2">Mirroring Field</span>
        <FieldDescriptor fieldName={field.calculate.params[0]} lookup={formLookup} />
      </div>
    );
  }

  return (
    <div className="calc-explain">
      {field.calculate.params.map((value, index) => {
        const paramValue = String(value);

        // Treat the last in the series differently (ie no operation comes after it)
        if (index === field.calculate.params.length - 1) {
          if (field.calculate.method === 'subtractToZero') {
            return (
              <React.Fragment key={`${paramValue}${index}`}>
                <FieldDescriptor fieldName={paramValue} lookup={formLookup} />
                <span className="explain">(minimum of zero)</span>
              </React.Fragment>
            );
          }
          return <FieldDescriptor key={`${paramValue}${index}`} fieldName={paramValue} lookup={formLookup} />;
        }

        return (
          <React.Fragment key={`${paramValue}${index}`}>
            <FieldDescriptor fieldName={paramValue} lookup={formLookup} />
            {operations[field.calculate.method]}
          </React.Fragment>
        );
      })}
    </div>
  );
};

CalcExplain.propTypes = {
  fieldName: PropTypes.string,
  formManager: PropTypes.object,
};

export default CalcExplain;
